import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { AlertCircle, Pencil, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const empty = {
  name: "",
  email: "",
  phone: "",
  password: "",
  role: "kasir",
  outlet_ids: [],
  permissions: [],
  pin: "",
  active: true,
};

export default function Users() {
  const { user: currentUser, entitlements, refresh } = useAuth();
  const [rows, setRows] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);

  const load = async () => {
    const [usersRes, outletsRes] = await Promise.all([api.get("/users"), api.get("/outlets")]);
    setRows(usersRes.data || []);
    setOutlets(outletsRes.data || []);
  };
  useEffect(() => { load(); }, []);

  const activeCount = useMemo(() => rows.filter((row) => row.active !== false).length, [rows]);
  const maxUsers = Number(entitlements?.max_users || 0);
  const atLimit = maxUsers > 0 && activeCount >= maxUsers;

  const openCreate = () => {
    if (atLimit) return toast.error(`Batas pengguna aktif tercapai (${activeCount}/${maxUsers})`);
    setForm(empty);
    setEditId(null);
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editId) {
        const body = { ...form };
        if (!form.password) delete body.password;
        if (!form.pin) delete body.pin;
        await api.patch(`/users/${editId}`, body);
      } else {
        const body = { ...form };
        if (!body.pin) delete body.pin;
        await api.post("/users", body);
      }
      toast.success("Pengguna tersimpan");
      setOpen(false);
      setForm(empty);
      setEditId(null);
      await load();
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Pengguna gagal disimpan");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus pengguna?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Pengguna terhapus");
      await load();
      await refresh();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Pengguna gagal dihapus");
    }
  };

  const toggleOutlet = (outletId) => setForm((current) => ({
    ...current,
    outlet_ids: current.outlet_ids.includes(outletId)
      ? current.outlet_ids.filter((id) => id !== outletId)
      : [...current.outlet_ids, outletId],
  }));

  return (
    <div className="p-6 lg:p-10">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6">
        <div>
          <div className="overline text-zinc-500">TIM</div>
          <h1 className="font-display font-black text-4xl tracking-tighter">Pengguna</h1>
          <div className="text-sm text-zinc-500 mt-2 tabular-nums">
            Penggunaan paket: <strong className="text-zinc-900">{activeCount}/{maxUsers || "-"}</strong> pengguna aktif
          </div>
        </div>
        <button onClick={openCreate} disabled={atLimit || !entitlements?.available} data-testid="user-add-btn" className="px-4 py-2.5 bg-[#002FA7] text-white text-sm font-semibold inline-flex items-center justify-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus className="h-4 w-4" /> Pengguna Baru
        </button>
      </div>

      {(atLimit || !entitlements?.available) && (
        <div className="mb-5 border border-amber-300 bg-amber-50 p-4 flex gap-3 text-sm text-amber-900">
          <AlertCircle className="h-5 w-5 shrink-0" />
          <div>
            <div className="font-semibold">{entitlements?.available ? "Kuota pengguna aktif sudah penuh" : "Langganan tidak aktif"}</div>
            <div className="mt-1">{entitlements?.available ? "Nonaktifkan pengguna yang tidak digunakan atau ubah paket untuk menambah pengguna baru." : entitlements?.reason}</div>
          </div>
        </div>
      )}

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-border"><tr>
            <th className="text-left p-3 overline">Nama</th>
            <th className="text-left p-3 overline">Kontak</th>
            <th className="text-left p-3 overline">Role</th>
            <th className="text-left p-3 overline">Outlet</th>
            <th className="text-left p-3 overline">Status</th>
            <th className="text-right p-3 overline">Aksi</th>
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-zinc-500">Belum ada pengguna</td></tr>}
            {rows.map((row) => (
              <tr key={row.id} className="border-b border-border">
                <td className="p-3 font-semibold">{row.name}{row.id === currentUser?.id && <span className="text-[10px] text-zinc-400 ml-2">ANDA</span>}</td>
                <td className="p-3 text-xs text-zinc-500">{row.email || row.phone}</td>
                <td className="p-3"><span className="text-xs uppercase tracking-wider font-semibold">{row.role}</span></td>
                <td className="p-3 text-xs text-zinc-500">{(row.outlet_ids || []).map((id) => outlets.find((outlet) => outlet.id === id)?.name).filter(Boolean).join(", ") || (row.role === "admin" ? "Semua" : "-")}</td>
                <td className="p-3"><span className={`text-xs px-2 py-0.5 ${row.active !== false ? "bg-emerald-100 text-emerald-800" : "bg-zinc-200 text-zinc-600"}`}>{row.active !== false ? "Aktif" : "Nonaktif"}</span></td>
                <td className="p-3 text-right">
                  <button onClick={() => {
                    setForm({
                      name: row.name,
                      email: row.email || "",
                      phone: row.phone || "",
                      password: "",
                      role: row.role,
                      outlet_ids: row.outlet_ids || [],
                      permissions: row.permissions || [],
                      pin: "",
                      active: row.active !== false,
                    });
                    setEditId(row.id);
                    setOpen(true);
                  }} className="p-1.5 hover:bg-zinc-100" data-testid={`user-edit-${row.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button disabled={row.id === currentUser?.id} onClick={() => del(row.id)} className="p-1.5 hover:bg-rose-100 text-rose-600 disabled:opacity-30 disabled:cursor-not-allowed" data-testid={`user-del-${row.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Buat"} Pengguna</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="overline">Nama</label><input data-testid="user-form-name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} className="w-full border border-border px-3 py-2 mt-1" /></div>
            <div><label className="overline">Email</label><input data-testid="user-form-email" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} className="w-full border border-border px-3 py-2 mt-1" /></div>
            <div><label className="overline">No HP</label><input data-testid="user-form-phone" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} className="w-full border border-border px-3 py-2 mt-1" /></div>
            <div><label className="overline">Password {editId && "(kosongkan jika tidak ganti)"}</label><input data-testid="user-form-password" type="password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} className="w-full border border-border px-3 py-2 mt-1" /></div>
            <div><label className="overline">Role</label>
              <select data-testid="user-form-role" value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })} className="w-full border border-border px-3 py-2 mt-1">
                <option value="admin">Admin</option><option value="supervisor">Supervisor</option><option value="kasir">Kasir</option>
              </select>
            </div>
            {(form.role === "supervisor" || form.role === "admin") && (
              <div><label className="overline">PIN Approval (Refund)</label>
                <input data-testid="user-form-pin" type="password" maxLength={8} value={form.pin || ""} onChange={(event) => setForm({ ...form, pin: event.target.value.replace(/\D/g, "") })} className="w-full border border-border px-3 py-2 mt-1 font-mono text-center text-lg tracking-[0.4em]" placeholder={editId ? "(kosongkan = tidak ubah)" : "4-8 digit"} />
                <div className="text-[10px] text-zinc-500 mt-1">PIN digunakan untuk menyetujui refund di POS.</div>
              </div>
            )}
            {editId && (
              <label className="col-span-2 flex items-center justify-between border border-border p-3 cursor-pointer">
                <div><div className="text-sm font-semibold">Pengguna aktif</div><div className="text-xs text-zinc-500">Pengguna nonaktif tidak menggunakan kuota paket dan tidak dapat login.</div></div>
                <input type="checkbox" disabled={editId === currentUser?.id} checked={form.active !== false} onChange={(event) => setForm({ ...form, active: event.target.checked })} />
              </label>
            )}
            <div className="col-span-2">
              <label className="overline">Outlet yang dapat diakses</label>
              <div className="mt-2 grid grid-cols-2 gap-2">
                {outlets.filter((outlet) => outlet.active !== false).map((outlet) => (
                  <label key={outlet.id} className="flex items-center gap-2 text-sm border border-border px-3 py-2">
                    <input type="checkbox" checked={form.outlet_ids.includes(outlet.id)} onChange={() => toggleOutlet(outlet.id)} data-testid={`user-outlet-${outlet.id}`} />{outlet.name}
                  </label>
                ))}
                {outlets.length === 0 && <div className="text-xs text-zinc-500">Belum ada outlet.</div>}
              </div>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setOpen(false)} className="flex-1 py-3 border border-border">Batal</button>
            <button onClick={save} data-testid="user-save-btn" className="flex-1 py-3 bg-[#002FA7] text-white font-semibold">Simpan</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
